import React, { useEffect, useMemo, useRef, useState } from "react";
import { Divider, Box, Button, Stack, Tooltip } from "@mui/material";
import { useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import {
  RiArrowLeftSFill,
  RiArrowRightSFill,
  RiPushpinFill,
} from "react-icons/ri";
import YesOrNoSwitch from "../../../UI/YesOrNoSwitch";
import { VerticalTab } from "../../../VerticalTabs";
import { useProject } from "../../../../contexts/project.context";
import { isAbbottClient } from "../../../../constants/abbottClient";
import { BudgetIcons } from "../../../../assets/styled-icons/budget";
import {
  BudgetItemTypeEnum,
  BudgetStatus,
  HeaderOptions,
} from "../../../../types/budget/budget";
import { createOptionsFromEnum } from "../../../../utils/formatters";
import { ReactComponent as DescriptionIcon } from "../../../../assets/styled-icons/Calendar.svg";
import BudgetDataModal from "../../../Project/components/BudgetDataModal";
import { TabActive } from "../../../../types";
import { ReactComponent as Unpinned } from "../../../../assets/styled-icons/unpinned.svg";
import { FaThumbtack } from "react-icons/fa";
import LeftSideBarHeader from "../LeftSideBarHeader";
import CalcOption from "../../Budget/LeftSideBarHeader/CalcOption";
import { useServices } from "../../../../contexts/ServicesContext/projectservices.context";
import { ServicesService } from "../../../../services/services.service";
import ServiceTotals from "../ServiceTotals";
import LeftSideBarHeaderCollapsed from "../../Budget/LeftSideBarHeader/Collapsed";
import { sortItemsByCategory } from "../../../../utils/category-sort";

type CategoriesSelectionProps = {
  onHoverChange?: (hovering: boolean) => void;
  onCollapseChange?: (collapsed: boolean) => void;
};

export const CategoriesSelection: React.FC<CategoriesSelectionProps> = ({
  onHoverChange,
  onCollapseChange,
}) => {
  //   const {
  //     activeBudget,
  //     setActiveCalcOption,
  //     activeBudgetDispatch,
  //     activeCalcOption,
  //     updateBudget,
  //     budgetList,
  //   } = useBudget();

  const {
    activeService,
    setActiveCalcOption,
    activeServiceDispatch,
    activeCalcOption,
    updateService,
    setCategoriesSnapshot,
  } = useServices();

  const { tabActive, client, project } = useProject();
  const showAbbottAdditionalInfo = useMemo(
    () => isAbbottClient(client?.clientSelected?.name ?? project?.client?.name),
    [client?.clientSelected?.name, project?.client?.name]
  );
  const [eventDataModal, setEventDataModal] = useState(false);
  const queryClient = useQueryClient();
  const firstRender = useRef(false);

  useEffect(() => {
    return () => {
      queryClient.removeQueries(["services-categories"]);
    };
  }, [queryClient]);

  const options = useMemo(() => {
    const itemTypeOption = createOptionsFromEnum(BudgetItemTypeEnum);
    const itemsWithType = itemTypeOption.map((el: any) => ({
      ...el,
      type: el.value,
    }));
    const reorderedOptions = sortItemsByCategory(itemsWithType, "type").filter(
      (el: any) => el.value !== "TRAVEL_INSURANCE"
    );

    return reorderedOptions.map((el: any, index: number) => {
      if (index === reorderedOptions.length - 1)
        return {
          title: el.label,
          value: el.value,
          description: "",
          icon: BudgetIcons[el.value],
          template: <ServiceTotals />,
        };
      return {
        title: el.label,
        value: el.value,
        description: "",
        icon: BudgetIcons[el.value],
        template: <></>,
      };
    });
  }, []);

  useEffect(() => {
    setCategoriesSnapshot(options || []);
  }, [options, setCategoriesSnapshot]);
  //

  const generalInfo = useMemo(() => {
    return activeService?.generalInfo?.[0];
  }, [activeService, tabActive]);

  const updateServiceLocal = async (payload: any) => {
    if (!activeService?._id) {
      toast.error("Serviço inválido para salvar os dados do evento");
      throw new Error("INVALID_SERVICE");
    }

    const result = await updateService(activeService._id, {
      ...activeService,
      generalInfo: [payload],
    });
    if (result == null) {
      throw new Error("SAVE_FAILED");
    }
    toast.success("Dados salvos com sucesso");
  };

  // --- visual states ---
  const [collapsed, setCollapsed] = useState(true);
  const [hoveringOverlay, setHoveringOverlay] = useState(false);

  useEffect(() => {
    onCollapseChange?.(collapsed);
  }, [collapsed]);

  const leaveTimer = useRef<number | null>(null);
  const clearLeaveTimer = () => {
    if (leaveTimer.current) window.clearTimeout(leaveTimer.current);
  };

  const containerRef = useRef<HTMLDivElement | null>(null);
  const handleRef = useRef<HTMLDivElement | null>(null);
  const [overlayPos, setOverlayPos] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });

  const overlayContentRef = useRef<HTMLDivElement | null>(null);
  const [overlayContentHeight, setOverlayContentHeight] = useState<number>(0);
  const overlayMode = collapsed;

  const openOverlayNearHandle = () => {
    if (!containerRef.current || !handleRef.current) return;
    const c = containerRef.current.getBoundingClientRect();
    const h = handleRef.current.getBoundingClientRect();
    const offset = 6;
    setOverlayPos({
      top: h.top - c.top,
      left: h.left - c.left - 320 - offset + h.width,
    });
  };

  const onHandleEnter = () => {
    if (!collapsed) return;
    clearLeaveTimer();
    openOverlayNearHandle();
    setHoveringOverlay(true);
    onHoverChange?.(true);
  };
  const onHandleLeave = () => {
    if (!collapsed) return;
    clearLeaveTimer();
    leaveTimer.current = window.setTimeout(() => {
      setHoveringOverlay(false);
      onHoverChange?.(false);
    }, 120);
  };
  const onOverlayEnter = () => {
    if (!collapsed) return;
    clearLeaveTimer();
    setHoveringOverlay(true);
    onHoverChange?.(true);
  };
  const onOverlayLeave = () => {
    if (!collapsed) return;
    clearLeaveTimer();
    leaveTimer.current = window.setTimeout(() => {
      setHoveringOverlay(false);
      onHoverChange?.(false);
    }, 120);
  };

  useEffect(() => {
    if (!hoveringOverlay) return;
    const el = overlayContentRef.current;
    if (!el) return;
    const measure = () => setOverlayContentHeight(el.scrollHeight);
    measure();
    let ro: ResizeObserver | null = null;
    if ("ResizeObserver" in window) {
      ro = new ResizeObserver(measure);
      ro.observe(el);
    } else {
      const id = setInterval(measure, 150);
      return () => clearInterval(id);
    }
    return () => {
      if (ro) ro.disconnect();
    };
  }, [hoveringOverlay, options, activeCalcOption, activeService]);

  useEffect(() => {
    const onResize = () => {
      if (hoveringOverlay && collapsed) openOverlayNearHandle();
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [hoveringOverlay, collapsed]);

  const ExpandedSidebar = (
    <Box
      ref={overlayContentRef}
      display="flex"
      flexDirection="column"
      gap={1.5}
      px="24px"
      pt="24px"
      pb="16px"
      borderRight="1px solid #DBDADE"
      sx={{
        bgcolor: "background.paper",
      }}
    >
      {/* <Box sx={{ display: "flex", flexDirection: "row" }}>
        <Button
          variant="outlined"
          startIcon={<DescriptionIcon width={16} height={16} />}
          sx={{
            width: "100%",
            height: 30,
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            overflow: "hidden",
            fontSize: "clamp(10px, 1.5vw, 14px)",
          }}
          onClick={() => setEventDataModal(true)}
        >
          Local do orçamento
        </Button>
      </Box>

      <LeftSideBarHeader
        value={(activeCalcOption as HeaderOptions) ?? "B"}
        onChange={(t) => setActiveCalcOption(t)}
      /> */}

      {/* <Divider /> */}

      <Stack spacing={1.5}>
        {options.map((el: any, index: any) => (
          <VerticalTab.Item key={index}>
            <VerticalTab.Icon
              icon={el.icon}
              isActive={false}
              color={
                activeService &&
                activeService?.itemTypeList?.[activeCalcOption]?.[el.value] ===
                  true
                  ? "#1361A4"
                  : activeService?.itemTypeList?.[activeCalcOption]?.[
                      el.value
                    ] === false
                  ? "#EFDBDB"
                  : undefined
              }
            />
            <VerticalTab.Content
              title={el.title}
              description={el.description}
            />
            <Box display="flex" flex={1} justifyContent="flex-end">
              <YesOrNoSwitch
                type={el.value}
                value={
                  activeService?.itemTypeList?.[activeCalcOption]?.[el.value]
                }
                disabled={
                  activeCalcOption === "budgetTotals" || !activeService?._id
                }
                setValue={(type: string, newValue: boolean) =>
                  activeServiceDispatch({
                    type: "YES_NO_SWITCH_CHANGE",
                    payload: { type, newValue, activeCalcOption },
                  })
                }
              />
            </Box>
          </VerticalTab.Item>
        ))}
      </Stack>
    </Box>
  );

  const CollapsedSidebar = (
    <Box
      display="flex"
      flexDirection="column"
      gap={1.5}
      px="12px"
      pt="24px"
      pb="44px"
      borderRight="1px solid #DBDADE"
    >
      {/* <Tooltip
        title={<span style={{ fontSize: "16px" }}>Dados do evento</span>}
        placement="right"
        PopperProps={{
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [0, -8],
              },
            },
          ],
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            width: 40,
            height: 40,
            border: "1px solid #1976D2",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 1,
            marginLeft: 1,
            cursor: "pointer",
          }}
          onClick={() => {
            setEventDataModal(true);
          }}
        >
          <DescriptionIcon width={16} height={16} />
        </Box>
      </Tooltip> */}

      {/* <Box sx={{ marginLeft: 1 }}>
       
        <LeftSideBarHeaderCollapsed
          value={(activeCalcOption as HeaderOptions) ?? "A"}
          onChange={(t) => setActiveCalcOption(t)}
        />
      </Box>

      <Divider /> */}
      <Stack spacing={1.5}>
        {options.map((el: any, index: any) => (
          <Tooltip
            title={<span style={{ fontSize: "16px" }}>{el?.title}</span>}
            placement="right"
            PopperProps={{
              modifiers: [
                {
                  name: "offset",
                  options: {
                    offset: [0, -8],
                  },
                },
              ],
            }}
          >
            <Box>
              <VerticalTab.Item
                key={index}
                onClick={() => {
                  if (
                    activeCalcOption === "budgetTotals" ||
                    !activeService?._id
                  )
                    return;

                  const currentItem: any =
                    activeService?.itemTypeList?.[activeCalcOption];
                  const currentItemValue = currentItem?.[el?.value];

                  const newValue =
                    currentItemValue === undefined
                      ? true
                      : currentItemValue === true
                      ? false
                      : true;

                  activeServiceDispatch({
                    type: "YES_NO_SWITCH_CHANGE",
                    payload: { type: el.value, newValue, activeCalcOption },
                  });
                }}
              >
                <VerticalTab.Icon
                  icon={el.icon}
                  isActive={false}
                  color={
                    activeService &&
                    activeService?.itemTypeList?.[activeCalcOption]?.[
                      el.value
                    ] === true
                      ? "#1361A4"
                      : activeService?.itemTypeList?.[activeCalcOption]?.[
                          el.value
                        ] === false
                      ? "#EFDBDB"
                      : undefined
                  }
                />
              </VerticalTab.Item>
            </Box>
          </Tooltip>
        ))}
      </Stack>
    </Box>
  );

  return (
    <Box
      ref={containerRef}
      sx={{
        display: "flex",
        flexDirection: "row",
        justifyContent: "flex-start",
        position: "relative",
        flex: 1,
        flexShrink: 0,
        width: !collapsed || hoveringOverlay ? 334 : "auto",
        minHeight: 0,
        height: "100%",
        alignSelf: "stretch",
      }}
    >
      <BudgetDataModal
        isOpen={eventDataModal}
        info={generalInfo}
        onClose={() => setEventDataModal(false)}
        onConfirm={updateServiceLocal}
        showAbbottAdditionalInfo={showAbbottAdditionalInfo}
      />

      {/* Área scrollável - scrollbar à esquerda (direction: rtl) */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          direction: "rtl",
          scrollbarWidth: "thin",
          scrollbarColor: "rgba(0, 0, 0, 0.25) transparent",
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(0, 0, 0, 0.25)",
            borderRadius: "3px",
          },
        }}
      >
        <Box
          sx={{
            direction: "ltr",
            visibility: collapsed && hoveringOverlay ? "hidden" : "visible",
            pointerEvents: collapsed && hoveringOverlay ? "none" : "auto",
          }}
        >
          {collapsed ? CollapsedSidebar : null}
          {!collapsed ? ExpandedSidebar : null}
        </Box>
      </Box>

      <Box
        ref={handleRef}
        sx={{
          alignSelf: "stretch",
          width: 14,
          flexShrink: 0,
          backgroundColor: collapsed ? "#536574" : "#1361A4",
          cursor: "pointer",
          borderTopRightRadius: "8px",
          borderBottomRightRadius: "8px",
          alignItems: "center",
          justifyContent: "center",
          display: "flex",
          zIndex: 2,
        }}
        onClick={() => {
          setCollapsed((c) => {
            if (c) return false;
            setHoveringOverlay(false);
            onHoverChange?.(false);
            return true;
          });
        }}
        onMouseEnter={onHandleEnter}
        onMouseLeave={onHandleLeave}
        title={
          collapsed ? "Expandir (hover) / Fixar (clique)" : "Recolher (clique)"
        }
      >
        {collapsed ? (
          <RiArrowRightSFill color="white" />
        ) : (
          <FaThumbtack color="white" size={14} />
        )}
      </Box>

      {overlayMode && hoveringOverlay && (
        <Box
          onMouseEnter={onOverlayEnter}
          onMouseLeave={onOverlayLeave}
          sx={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: 334,
            display: "flex",
            flexDirection: "row",
            zIndex: (theme) => theme.zIndex.drawer + 1,
            boxShadow:
              "0px 3px 6px rgba(0,0,0,0.16), 0px 6px 12px rgba(0,0,0,0.12)",
            borderRadius: 1,
            bgcolor: "background.paper",
          }}
        >
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              minWidth: 0,
              overflowY: "auto",
              overflowX: "hidden",
              width: "100%",
              maxWidth: 320,
              direction: "rtl",
              scrollbarWidth: "thin",
              scrollbarColor: "rgba(0, 0, 0, 0.25) transparent",
              "&::-webkit-scrollbar": { width: 6 },
              "&::-webkit-scrollbar-track": { background: "transparent" },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "rgba(0, 0, 0, 0.25)",
                borderRadius: "3px",
              },
            }}
          >
            <Box sx={{ direction: "ltr" }}>{ExpandedSidebar}</Box>
          </Box>
          <Box
            sx={{
              flexShrink: 0,
              width: 14,
              minHeight: "100%",
              backgroundColor: "#536574",
              cursor: "pointer",
              borderTopRightRadius: "8px",
              borderBottomRightRadius: "8px",
              alignItems: "center",
              justifyContent: "center",
              display: "flex",
            }}
            onMouseEnter={onOverlayEnter}
            onMouseLeave={onOverlayLeave}
            onClick={() => setCollapsed(false)}
            title="Fixar (clique)"
          >
            <Unpinned />
          </Box>
        </Box>
      )}
    </Box>
  );
};
